import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import {
  Input, CustomSelect, ConfirmModal, DialogShell, DialogHeader, DialogFooter, dialogButtonClass, FormFieldWithError, LoadingButton, Amount,
} from '@redpanda/ui';
import { useNumberInput, useUnsavedChangesWarning } from '@redpanda/utils';
import { createCreditNote } from '../services/api';
import { Invoice, outstandingOf, updateInvoiceInLists } from '../stores/invoiceStore';
import { useBankAccountOptionsStore } from '../stores/bankAccountStore';
import { notifyDataChanged } from '../utils/dataEvents';
import { apiErrorMessage } from '../utils/apiError';
import { todayInput, toUtcMidnight, dateInputValue } from '../utils/dates';

interface CreditNoteDialogProps {
  invoice: Invoice;
  onClose: () => void;
}

interface FormData {
  date: string;
  subtotal: string;
  reason: string;
  refundBankAccountId: string;
}

const round2 = (value: number) => Math.round(value * 100) / 100;

/**
 * 開立折讓（同 Xero、QuickBooks 的 Credit Note；台灣「銷貨退回及折讓」）
 * 輸入未稅金額，稅額依原發票稅率；先沖抵未收金額，超過的部分退款（要選退款帳戶）
 * 後端自動記帳：借收入科目、銷項稅額／貸應收帳款、銀行存款；營業稅試算在折讓日期的期別扣除
 */
const CreditNoteDialog: React.FC<CreditNoteDialogProps> = ({ invoice, onClose }) => {
  const { t } = useTranslation();
  const [saving, setSaving] = useState(false);
  const bankAccounts = useBankAccountOptionsStore(state => state.accounts).filter(account => account.isActive);
  const fetchBankAccounts = useBankAccountOptionsStore(state => state.fetchAll);
  const today = todayInput();
  const invoiceDate = dateInputValue(invoice.date);
  const taxRate = Number(invoice.taxRate);
  const creditable = round2(Number(invoice.subtotal) - (invoice.creditNotes ?? []).reduce((sum, note) => sum + Number(note.subtotal), 0));
  const outstanding = outstandingOf(invoice);

  const { register, handleSubmit, formState: { errors, isDirty }, setValue, watch } = useForm<FormData>({
    defaultValues: { date: today < invoiceDate ? invoiceDate : today, subtotal: '', reason: '', refundBankAccountId: '' },
  });
  const amountInput = useNumberInput('decimal');
  useEffect(() => {
    fetchBankAccounts();
  }, [fetchBankAccounts]);

  const subtotal = Number(watch('subtotal')) || 0;
  const taxAmount = round2(subtotal * taxRate);
  const total = round2(subtotal + taxAmount);
  const refund = Math.max(0, round2(total - outstanding));

  const { handleClose, showConfirm, handleConfirmClose, handleCancelClose } = useUnsavedChangesWarning(isDirty && !saving, onClose);

  const onSubmit = async (data: FormData) => {
    setSaving(true);
    try {
      const { data: saved } = await createCreditNote(invoice.id, {
        date: toUtcMidnight(data.date),
        subtotal: Number(data.subtotal),
        reason: data.reason.trim() || null,
        refundBankAccountId: refund > 0 ? data.refundBankAccountId : null,
      });
      updateInvoiceInLists(invoice.id, saved);
      toast.success(t('invoices.creditNote.success'));
      notifyDataChanged('invoices');
      notifyDataChanged('journals');
      if (refund > 0) notifyDataChanged('bankTransactions');
      onClose();
    } catch (error) {
      toast.error(apiErrorMessage(error, 'common.messages.createFailed'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <DialogShell onClose={handleClose} closeDisabled={saving} className="max-w-lg">
        <DialogHeader title={t('invoices.creditNote.title')} onClose={handleClose} closeDisabled={saving} closeLabel={t('common.actions.close')} />
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="p-6 space-y-5">
            <p className="text-sm text-fg-secondary">
              {invoice.number} · {invoice.customerName} · {t('invoices.payment.outstanding')} <span className="font-medium text-fg"><Amount value={outstanding} /></span>
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FormFieldWithError label={t('invoices.creditNote.date')} required error={errors.date?.message} htmlFor="creditDate">
                <Input
                  id="creditDate"
                  type="date"
                  {...register('date', {
                    required: t('common.forms.validation.required'),
                    validate: value => value >= invoiceDate || t('errors.CREDIT_BEFORE_INVOICE'),
                  })}
                  variant={errors.date ? 'error' : 'default'}
                />
              </FormFieldWithError>
              <FormFieldWithError
                label={t('invoices.creditNote.subtotal')}
                required
                error={errors.subtotal?.message}
                helpText={t('invoices.creditNote.subtotalHint', { amount: creditable.toLocaleString() })}
                htmlFor="creditSubtotal"
              >
                <Input
                  id="creditSubtotal"
                  inputMode="decimal"
                  className="text-right tabular-nums"
                  {...amountInput.bind(register('subtotal', {
                    validate: value => {
                      const amount = Number(value);
                      if (!(amount > 0)) return t('bankTransactions.dialog.amountMin');
                      return amount <= creditable + 0.005 || t('errors.CREDIT_EXCEEDS_INVOICE');
                    },
                  }))}
                  variant={errors.subtotal ? 'error' : 'default'}
                />
              </FormFieldWithError>
            </div>
            <FormFieldWithError label={t('invoices.creditNote.reason')} htmlFor="creditReason">
              <Input id="creditReason" placeholder={t('invoices.creditNote.reasonPlaceholder')} {...register('reason')} />
            </FormFieldWithError>

            <dl className="ml-auto w-full max-w-xs space-y-1.5 border-t border-line pt-3 text-sm">
              <div className="flex justify-between"><dt className="text-fg-secondary">{t('invoices.taxAmount')}</dt><dd><Amount value={taxAmount} /></dd></div>
              <div className="flex justify-between font-semibold text-fg"><dt>{t('invoices.creditNote.total')}</dt><dd><Amount value={total} /></dd></div>
              {refund > 0 && (
                <div className="flex justify-between text-fg-secondary"><dt>{t('invoices.creditNote.refund')}</dt><dd><Amount value={refund} /></dd></div>
              )}
            </dl>

            {refund > 0 && (
              <FormFieldWithError
                label={t('invoices.creditNote.refundAccount')}
                required
                error={errors.refundBankAccountId?.message}
                helpText={t('invoices.creditNote.refundHint')}
                htmlFor="refundBank"
              >
                <CustomSelect
                  id="refundBank"
                  value={watch('refundBankAccountId')}
                  onChange={value => setValue('refundBankAccountId', value, { shouldDirty: true, shouldValidate: true })}
                  placeholder={t('invoices.payment.selectBankAccount')}
                  options={bankAccounts.map(account => ({ value: account.id, label: `${account.accountName}（${account.bankName}）` }))}
                />
                <input type="hidden" {...register('refundBankAccountId', { validate: value => refund <= 0 || !!value || t('invoices.payment.selectBankAccount') })} />
              </FormFieldWithError>
            )}
          </div>
          <DialogFooter>
            <button type="button" onClick={handleClose} disabled={saving} className={dialogButtonClass.cancel}>
              {t('common.actions.cancel')}
            </button>
            <LoadingButton type="submit" loading={saving} className={dialogButtonClass.primary}>
              {t('invoices.creditNote.submit')}
            </LoadingButton>
          </DialogFooter>
        </form>
      </DialogShell>

      <ConfirmModal
        isOpen={showConfirm}
        onClose={handleCancelClose}
        onConfirm={handleConfirmClose}
        title={t('modal:unsavedChanges.title')}
        message={t('modal:unsavedChanges.message')}
        confirmText={t('modal:unsavedChanges.confirmButton')}
        cancelText={t('modal:unsavedChanges.cancelButton')}
        t={t}
      />
    </>
  );
};

export default CreditNoteDialog;
